/**
 * Factora Single-CTA Page
 * Main page for Factora credit score intake and results
 */

import React, { useState } from 'react';
import { Hero } from '../components/factora/Hero';
import { IntakeForm } from '../components/factora/IntakeForm';
import { ProfileCard } from '../components/factora/ProfileCard';
import { fetchWithRetries } from '../utils/fetchWithRetries';
import '../styles/factora.css';

type ViewState = 'hero' | 'form' | 'results';

interface ScoreResponse {
  borrower: {
    borrower_id: string;
    full_name: string;
    national_id: string;
    email: string;
    phone: string;
    created_at: string;
  };
  enrichment?: {
    ip_address?: string;
    user_agent?: string;
    timestamp?: string;
    source?: string;
    notes?: string;
  };
  score: {
    factora_score: number;
    score_band?: string;
    risk_level?: string;
  };
  correlation_id?: string;
  _isDemo?: boolean;
}

export const FactoraPage: React.FC = () => {
  const [view, setView] = useState<ViewState>('hero');
  const [results, setResults] = useState<ScoreResponse | null>(null);

  const handleGetStarted = () => {
    setView('form');
  };

  const handleFormSubmit = async (formData: any) => {
    // Check if this is a demo profile passed from IntakeForm
    if (formData._profileData) {
      console.log('[FactoraPage] Received demo profile from IntakeForm');
      setResults(formData._profileData);
      setView('results');
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 
      import.meta.env.NEXT_PUBLIC_SUPABASE_URL ||
      'https://rzashahhkafjicjpupww.supabase.co';

    const scoreCheckerUrl = import.meta.env.VITE_PROFILE_FN_URL || 
      import.meta.env.VITE_SCORE_CHECKER_URL ||
      `${supabaseUrl}/functions/v1/score-checker`;

    const scoreBrokerUrl = `${supabaseUrl}/functions/v1/score-broker`;

    const scoreMode = import.meta.env.VITE_SCORE_MODE || 'demo';

    console.log(`[FactoraPage] Score mode: ${scoreMode}`);

    const payload = {
      full_name: formData.full_name,
      national_id: formData.national_id,
      email: formData.email,
      phone: formData.phone,
      consent: formData.consent,
      consent_text: 'I consent to the processing of my personal data for credit evaluation purposes',
      intake_source: 'github_pages_demo',
      intake_form_version: 'v1',
      intent_financing: formData.intent_financing,
      prior_borrowing: formData.prior_borrowing,
    };

    try {
      let authToken = '';
      let correlationId = '';

      // Secure mode: Get token from score-broker first
      if (scoreMode === 'secure') {
        console.log('[FactoraPage] Secure mode: Requesting token from score-broker');
        
        const brokerPayload = {
          full_name: formData.full_name,
          email: formData.email,
          national_id: formData.national_id,
        };

        const brokerResult = await fetchWithRetries(scoreBrokerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
            'x-factora-client': 'web-app/1.0',
          },
          body: JSON.stringify(brokerPayload),
          maxAttempts: 3,
        });

        if (!brokerResult.ok) {
          throw new Error(
            `Token broker error (${brokerResult.status}): ${brokerResult.json?.error || 'Unknown error'}. Correlation ID: ${brokerResult.correlationId}`
          );
        }

        const tokenData = brokerResult.json;
        authToken = tokenData.token;
        correlationId = tokenData.correlation_id;

        console.log('[FactoraPage] Token acquired:', {
          ttl_seconds: tokenData.ttl_seconds,
          correlation_id: correlationId,
        });
      }

      // Call score-checker (with or without Authorization header)
      console.log('[FactoraPage] Submitting to score-checker:', scoreCheckerUrl);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        'x-factora-client': 'web-app/1.0',
      };

      // Add Authorization header in secure mode
      if (scoreMode === 'secure' && authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Add correlation_id if available
      if (correlationId) {
        headers['x-correlation-id'] = correlationId;
      }

      const result = await fetchWithRetries(scoreCheckerUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        maxAttempts: 3,
      });

      console.log('[FactoraPage] Fetch result:', {
        ok: result.ok,
        status: result.status,
        correlationId: result.correlationId,
      });

      if (!result.ok) {
        // Map status codes to user-friendly messages
        if (result.status === 401 || result.status === 403) {
          throw new Error(
            'Service unavailable — authentication required. Please contact support.'
          );
        } else if (result.status >= 500) {
          throw new Error(
            `Temporary server error (${result.status}) — please try again. Correlation ID: ${result.correlationId}`
          );
        } else if (result.status === 0 || result.error) {
          throw new Error(
            `Network error — unable to reach server. Please check your connection and try again.`
          );
        } else {
          throw new Error(
            `Error ${result.status}: ${result.json?.error || 'Unknown error'}. Correlation ID: ${result.correlationId}`
          );
        }
      }

      const data = result.json;

      // Defensive parsing as per mandate
      const borrower = data?.borrower || data?.data?.borrower || data?.payload?.borrower;
      const score = data?.score || data?.data?.score || data?.payload?.score;

      if (!borrower || !score) {
        console.error('[FactoraPage] Invalid response schema:', data);
        throw new Error('Invalid response from server - missing required fields');
      }

      // Add correlation ID to console for debugging
      console.log('[FactoraPage] Success! Correlation ID:', result.correlationId);
      console.log('[FactoraPage] Profile data:', {
        borrower_id: borrower.borrower_id,
        score: score.factora_score,
      });

      setResults({
        borrower,
        score,
        enrichment: data.enrichment || data?.data?.enrichment || {},
        correlation_id: result.correlationId,
      });
      setView('results');
    } catch (error) {
      console.error('[FactoraPage] Score check failed:', error);
      throw error; // Re-throw to let IntakeForm handle it
    }
  };

  const handleCancel = () => {
    setView('hero');
  };

  const handleReset = () => {
    setResults(null);
    setView('hero');
  };

  return (
    <div className="factora-page">
      <div className="factora-container">
        {view === 'hero' && <Hero onGetStarted={handleGetStarted} />}
        
        {view === 'form' && (
          <IntakeForm onSubmit={handleFormSubmit} onCancel={handleCancel} />
        )}
        
        {view === 'results' && results && (
          <ProfileCard
            borrower={results.borrower}
            enrichment={results.enrichment}
            score={results.score}
            onReset={handleReset}
            isDemo={results._isDemo}
          />
        )}
      </div>
    </div>
  );
};
